import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { useProjectEmployeesDataTable } from 'generated/resources/datatable.graphql'
import { ProjectForSelectFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { AssignedFilter } from './Filters/AssignedFilter'
import NoEmployeesMessage from './TableBody/NoEmployeesMessage'
import { projectWorkerProjections } from './projections'
import { IProjectWorkersPanelData } from './types'

interface ProjectEmployeesDataTableProps {
  canAssignEmployees: boolean
  canAssignSingleEmployee: boolean
  canExportData: boolean
  canSeeLaborCosts: boolean
  canSeeSpendings: boolean
  projectId: number
  projectCurrency: string
  showProjectWorkersPanel: (values: IProjectWorkersPanelData) => void
}

const ProjectEmployeesDataTable = ({
  canAssignEmployees,
  canAssignSingleEmployee,
  canExportData,
  canSeeLaborCosts,
  canSeeSpendings,
  projectId,
  projectCurrency,
  showProjectWorkersPanel,
}: ProjectEmployeesDataTableProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const params = useParams()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { hasExpenses, hasFlexibleTracking, project } = useOutletContext<{
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    project: ProjectForSelectFragment
  }>()

  const source = useProjectEmployeesDataTable(
    projectWorkerProjections({
      canAssignSingleEmployee,
      canSeeLaborCosts,
      canSeeSpendings,
      projectCurrency,
      showProjectWorkersPanel,
    }),
    {
      variables: {
        projectIds: [projectId],
        hasSpendings: canSeeSpendings,
        includeCost: canSeeLaborCosts,
        employeeName: debouncedSearch,
      },
    }
  )

  const filters = [AssignedFilter(source)]

  return (
    <DataTable.Provider
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 20,
        infiniteScroll: false,
      }}
      sort={{
        path: 'assigned',
      }}
      filters={filters}
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableExport
          title={i18n.t('project_management.details.employee_tab')}
          customPopoverOptions={
            canExportData && (
              <PopoverMenu.TouchableOption
                title={i18n.t('project_management.table.projects_list.table_options.export.title')}
                description={i18n.t(
                  'project_management.table.projects_list.table_options.export.description'
                )}
                onClick={() => {
                  track(trackingEvents.projectManagement.projects.downloadProjectsReportStart)
                  navigate(
                    navigation.projects.id.details.employees.export.fullPath({ id: projectId }),
                    { state: { projectForSelect: project } }
                  )
                }}
              />
            )
          }
          search={{
            placeholder: i18n.t('common.search_by'),
            value: search,
            onChange: onSearch,
          }}
          actions={
            canAssignEmployees && (
              <Button.Router
                to={navigation.projects.id.details.employees.workers.fullPath({
                  id: Number(params.id),
                })}
                onClick={() => {
                  track(
                    trackingEvents.projectManagement.projectWorker.assignEmployeesToProjectStart
                  )
                }}
                type="secondary"
                iconName="add"
              >
                {i18n.t('project_management.details.assign_employees')}
              </Button.Router>
            )
          }
        />
        <DataTable.Table emptyState={<NoEmployeesMessage />} autoWidth />
      </Box>
      <Outlet context={{ hasExpenses, hasFlexibleTracking }} />
    </DataTable.Provider>
  )
}

export default observer(ProjectEmployeesDataTable)
